import { json, jsonParseLinter } from '@codemirror/lang-json';
import { linter, lintGutter } from '@codemirror/lint';
import { EditorView } from '@codemirror/view';
import CodeMirror from '@uiw/react-codemirror';
import { Check, TriangleAlert, WrapText } from 'lucide-react';
import React, { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { jsonEditorTheme, jsonHighlighting } from './json-editor-theme';

export interface JsonEditorMeta {
  valid: boolean;
  error?: string;
  parsed?: unknown;
}

export interface JsonEditorProps {
  value: string;
  onChange?: (value: string, meta: JsonEditorMeta) => void;
  minHeight?: string;
  maxHeight?: string;
  /** Grow to fill the parent (parent must be a flex column with a bounded height). */
  fill?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  autoFocus?: boolean;
  /** Show the Format button + validity badge. Ignored when readOnly. Default true. */
  showToolbar?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
}

/**
 * Compute validity for a JSON string. An empty/whitespace-only string is treated
 * as valid with `parsed: undefined` so callers keep their own required/optional rule.
 */
export function inspectJson(value: string): JsonEditorMeta {
  if (!value.trim()) return { valid: true, parsed: undefined };
  try {
    return { valid: true, parsed: JSON.parse(value) };
  } catch (e) {
    return { valid: false, error: e instanceof Error ? e.message : 'Invalid JSON' };
  }
}

/**
 * Shared CodeMirror-based JSON editor (ported from the admin dashboard). Drop-in
 * replacement for a controlled `<textarea>`: `value` is the raw JSON string,
 * `onChange` reports the new string plus validity metadata so parents can gate
 * their Save button on `meta.valid`.
 */
export function JsonEditor({
  value,
  onChange,
  minHeight = '200px',
  maxHeight,
  fill = false,
  placeholder,
  readOnly = false,
  autoFocus = false,
  showToolbar = true,
  id,
  className,
  'aria-label': ariaLabel,
}: JsonEditorProps) {
  const extensions = useMemo(
    () => [
      json(),
      jsonHighlighting,
      linter(jsonParseLinter()),
      lintGutter(),
      EditorView.lineWrapping,
    ],
    [],
  );

  const meta = useMemo(() => inspectJson(value), [value]);
  const toolbar = showToolbar && !readOnly;

  const handleFormat = () => {
    if (!onChange || !meta.valid || meta.parsed === undefined) return;
    const formatted = JSON.stringify(meta.parsed, null, 2);
    onChange(formatted, inspectJson(formatted));
  };

  return (
    <div
      className={cn(
        'overflow-hidden rounded-md border border-input',
        fill && 'flex h-full flex-col',
        className,
      )}
    >
      {toolbar && (
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-muted/50 px-2 py-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={handleFormat}
            disabled={!meta.valid || meta.parsed === undefined}
          >
            <WrapText className="mr-1 h-3 w-3" />
            Format
          </Button>
          {value.trim() ? (
            meta.valid ? (
              <span className="flex items-center gap-1 text-xs text-positive-foreground">
                <Check className="h-3 w-3" />
                Valid
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-destructive">
                <TriangleAlert className="h-3 w-3" />
                Invalid
              </span>
            )
          ) : null}
        </div>
      )}
      <CodeMirror
        id={id}
        aria-label={ariaLabel}
        value={value}
        theme={jsonEditorTheme}
        extensions={extensions}
        readOnly={readOnly}
        editable={!readOnly}
        autoFocus={autoFocus}
        placeholder={placeholder}
        className={cn(fill && 'min-h-0 flex-1')}
        height={fill ? '100%' : undefined}
        minHeight={fill ? undefined : minHeight}
        maxHeight={maxHeight}
        basicSetup={{
          lineNumbers: true,
          foldGutter: true,
          bracketMatching: true,
          closeBrackets: !readOnly,
          highlightActiveLine: !readOnly,
          highlightActiveLineGutter: !readOnly,
          autocompletion: false,
        }}
        onChange={val => onChange?.(val, inspectJson(val))}
      />
    </div>
  );
}
